import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { candidaciesMinimalQueryKey } from "@/hooks/queries/useGetCandidaciesMinimal";
import { candidacyInfoQueryKey } from "@/hooks/queries/useGetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import {
  CandidacyOwner,
  CandidacyInfoResponse,
  GetOtherCandidaciesResponse,
  HttpResponse,
  MinimalCandidacy,
} from "@/types";

export function useDeleteCandidacyOwner() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      candidacyId,
      id,
    }: {
      candidacyId: string;
      id: number;
    }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}/candidacy_owners/${id.toString()}`;
      const res = await guacHttpClient.delete<
        CandidacyOwner,
        HttpResponse<CandidacyOwner>
      >(path);

      return res.data;
    },
    onMutate: async ({ candidacyId, id }) => {
      // These are the two places where assigning and removing can happen.
      const minimalKey = candidaciesMinimalQueryKey(
        currentCompanyId,
        currentJobId,
      );
      const infoKey = candidacyInfoQueryKey(
        currentCompanyId,
        currentJobId,
        candidacyId,
      );

      await queryClient.cancelQueries({ queryKey: minimalKey });
      await queryClient.cancelQueries({ queryKey: infoKey });
      await queryClient.cancelQueries({ queryKey: ["other-candidacies"] });

      const previousMinimal = queryClient.getQueriesData<MinimalCandidacy[]>({
        queryKey: minimalKey,
      });
      const previousInfo =
        queryClient.getQueryData<CandidacyInfoResponse>(infoKey);
      const previousOtherCandidacies = queryClient.getQueriesData<
        GetOtherCandidaciesResponse[]
      >({
        queryKey: ["other-candidacies"],
      });

      queryClient.setQueriesData<MinimalCandidacy[]>(
        { queryKey: minimalKey },
        (old) =>
          old?.map((c) =>
            c.id === candidacyId
              ? {
                  ...c,
                  owners: c.owners.filter((o) => o.id !== id),
                }
              : c,
          ),
      );

      queryClient.setQueryData<CandidacyInfoResponse>(infoKey, (old) =>
        old ? { ...old, candidacyOwners: [] } : old,
      );
      queryClient.setQueriesData<GetOtherCandidaciesResponse[]>(
        { queryKey: ["other-candidacies"] },
        (old) =>
          old?.map((candidacy) =>
            candidacy.id === candidacyId
              ? {
                  ...candidacy,
                  candidacyOwners: candidacy.candidacyOwners.filter(
                    (owner) => owner.id !== id,
                  ),
                }
              : candidacy,
          ),
      );

      return {
        previousMinimal,
        previousInfo,
        previousOtherCandidacies,
        minimalKey,
        infoKey,
      };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      // Reset on error.
      context.previousMinimal.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      queryClient.setQueryData(context.infoKey, context.previousInfo);
      context.previousOtherCandidacies.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: candidaciesMinimalQueryKey(currentCompanyId, currentJobId),
      });
      void queryClient.invalidateQueries({
        queryKey: candidacyInfoQueryKey(
          currentCompanyId,
          currentJobId,
          variables.candidacyId,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: ["my-candidacies"],
      });
      void queryClient.invalidateQueries({ queryKey: ["other-candidacies"] });
    },
  });

  return mutation;
}
